import { FieldName } from "../../../Schema";
import { CsvFieldStructure, CsvFlowOverrides, CsvFlows, CsvStructure } from "../../../csv/structure";
import { qualificationStatusOptions } from "../../common/fields/qualificationStatus";
import { controlledEquipmentStatusOptions } from "../fields/controlledEquipmentStatus";

/**
 * The qualification column carries a value from one of two enums, so its label is looked
 * up across both — and an import has to accept either. Order matters only if the two ever
 * share a value, which they do for "NA", and both spell that the same way.
 */
export const qualificationCellOptions = [
  ...qualificationStatusOptions,
  ...controlledEquipmentStatusOptions,
];

/**
 * A column that needs nothing said about it beyond being one.
 *
 * Most do. The type, the enum options, whether the column is mandatory and how a value
 * becomes a cell are all read from the field's own schema, so an entry only spells more than
 * this out when its CSV behaviour genuinely differs.
 *
 * @param name - The field to make a column of.
 * @returns The structure entry.
 */
const column = (name: FieldName): CsvFieldStructure => ({
  name,
  csv: {
    displayed: true,
  },
});

/**
 * The instance's own display name, which the master data calls linkedInstanceDisplay.
 *
 * A CSV is produced from the search index, where only the internal name is stored and the
 * master data is not available, so the one instance whose two names differ is mapped here.
 */
const linkedInstanceColumn: CsvFieldStructure = {
  name: "linkedInstance",
  csv: {
    displayed: true,
    translator: (value) => {
      if (value === null || value === undefined || value === "") {
        return "";
      }
      const name = String(value);
      return name === "Molecular" ? "RMD SAP" : name;
    },
  },
};

/**
 * One column standing for two fields: an uncontrolled equipment reports its qualification
 * status here, a controlled one its calibration state.
 */
const qualificationStatusColumn: CsvFieldStructure = {
  name: "qualificationStatus",
  csv: {
    displayed: true,
    // The value may have come from either enum, so neither field's own options can
    // resolve it alone. An unrecognised value falls through unchanged rather than
    // being blanked.
    translator: (value) => {
      if (value === null || value === undefined || value === "") {
        return "";
      }
      const match = qualificationCellOptions.find((option) => option.value === value);
      return match ? match.label : String(value);
    },
  },
  dataTransform: {
    // A calibrated equipment reports its calibration state in this column; only an
    // uncontrolled one ("NA") reports its own qualification status.
    toCsv: {
      getter: ({ qualificationStatus, controlledEquipmentStatus }) =>
        controlledEquipmentStatus && controlledEquipmentStatus !== "NA" ?
          controlledEquipmentStatus :
          qualificationStatus,
      fields: ["qualificationStatus", "controlledEquipmentStatus"],
    },
  },
};

/**
 * The bulk-change CSV, in column order.
 *
 * Downloaded, edited and re-uploaded to change equipment that already exists, which is why
 * `id` leads: it is how a row says which equipment it is.
 *
 * **Position in this array is the column's identity.** A row's cell *i* is read back as this
 * array's field *i*, and the label row is written only so a person can read the file. That
 * makes the array append-only: inserting a column mid-list moves every column after it, and
 * a file already downloaded would then be read one column out from where it was written.
 * Appending is safe, and a file written before the append still reads, its trailing fields
 * simply absent.
 */
export const equipmentCsvStructure: CsvStructure = [
  column("id"),
  linkedInstanceColumn,
  column("secondaryIDMapping"),
  column("equipmentId"),
  column("equipmentCategory"),
  column("manufacturer"),
  column("equipmentModel"),
  column("variant"),
  column("description"),
  column("equipmentType"),
  column("equipmentNickName"),
  column("siteName"),
  column("buildingLocation"),
  column("floor"),
  column("room"),
  column("labArea"),
  column("belongingToGroup"),
  column("serialNumber"),
  column("materialNumber"),
  column("instrumentGTIN"),
  column("instrumentRUDI"),
  column("cluster"),
  column("comment"),
  column("responsiblePerson"),
  column("responsibleProxy"),
  column("systemOwner"),
  column("contactPerson"),
  column("gxpRelevant"),
  qualificationStatusColumn,
  column("dateOfLastMaintanance"),
  column("maintenancePerformedBy"),
  column("dateOfLastCalibration"),
  column("systemStatus"),
  column("softwareVersion"),
  column("configurationBaseline"),
  column("configurationDetails"),
  column("functionalLocation"),
  column("network"),
  column("costCenter"),
  column("isBookable"),
  column("checkInRequired"),
  column("checkInStartPeriod"),
  column("checkInGracePeriod"),
  column("isHazardous"),
  column("shouldAssisted"),
  column("isVisualized"),
  column("inventoryNumber"),
  column("sop"),
  column("portability"),
];

/**
 * The create template, in column order.
 *
 * Downloaded empty and filled in to add equipment that does not exist yet, which is why it
 * carries no `id`: every row of it is a create. Append-only for the same reason as the
 * bulk-change structure.
 *
 * The order is the one the hand-maintained template file used, kept so that the sheet a user
 * knows does not rearrange itself under them. It is otherwise the bulk-change field set
 * without `id` and without `controlledEquipmentStatus` — the latter because it exists to
 * carry a calibration state back out of the repository, and a new equipment has none.
 */
export const equipmentTemplateCsvStructure: CsvStructure = [
  linkedInstanceColumn,
  column("secondaryIDMapping"),
  column("equipmentId"),
  column("equipmentCategory"),
  column("manufacturer"),
  column("equipmentModel"),
  column("variant"),
  column("description"),
  column("equipmentType"),
  column("equipmentNickName"),
  column("siteName"),
  column("buildingLocation"),
  column("floor"),
  column("room"),
  column("labArea"),
  column("belongingToGroup"),
  column("serialNumber"),
  column("materialNumber"),
  column("instrumentGTIN"),
  column("instrumentRUDI"),
  column("cluster"),
  column("comment"),
  column("responsiblePerson"),
  column("responsibleProxy"),
  column("systemOwner"),
  column("contactPerson"),
  column("gxpRelevant"),
  qualificationStatusColumn,
  column("dateOfLastMaintanance"),
  column("maintenancePerformedBy"),
  column("dateOfLastCalibration"),
  column("systemStatus"),
  column("softwareVersion"),
  column("configurationBaseline"),
  column("configurationDetails"),
  column("functionalLocation"),
  column("network"),
  column("costCenter"),
  column("isBookable"),
  column("checkInRequired"),
  column("checkInStartPeriod"),
  column("checkInGracePeriod"),
  column("isHazardous"),
  column("shouldAssisted"),
  column("isVisualized"),
  column("inventoryNumber"),
  column("sop"),
  column("portability"),
];

/**
 * Worked rows shipped with the template, as the **cell text** they should appear as.
 *
 * Deliberately not field values run through the translators. These are documentation — they
 * show a user what a secondary-ID mapping or an SOP list is meant to look like — and text
 * says exactly that, where a value would be at the mercy of whichever option list happened to
 * resolve. A field with nothing to demonstrate is simply absent and writes an empty cell.
 *
 * Keyed by field name rather than by position, so they follow the structure when it changes
 * instead of quietly shifting one column out of line.
 */
export const equipmentTemplateExamples: Record<FieldName, string>[] = [
  {
    linkedInstance: "RMD SAP",
    secondaryIDMapping: "equipmentId",
    equipmentCategory: "Analyzer",
    manufacturer: "Roche",
    equipmentModel: "cobas 5800",
    variant: "Pilot",
    siteName: "Rotkreuz",
    serialNumber: "100120231",
    materialNumber: "8988277001",
    equipmentNickName: "Asterix",
    instrumentGTIN: "4020000",
    equipmentId: "6005289921",
    instrumentRUDI: "GTIN^4020000^5012091",
    cluster: "C4",
    comment: "import pml data",
    belongingToGroup: "DSRCH",
    buildingLocation: "Let-1",
    floor: "EG",
    room: "130",
    responsiblePerson: "Hagemann/Richter",
    systemOwner: "Hagemann/Richter",
    responsibleProxy: "Robert Jenkin",
    sop: "F_RT0001_SOP_DE,F_RT0006_SOP_DE,F_RT0020_SOP_DE",
    gxpRelevant: "Yes",
    qualificationStatus: "NA",
    dateOfLastMaintanance: "19-02-2022",
    softwareVersion: "1",
    configurationBaseline: "3.5",
    systemStatus: "Ready to use",
    functionalLocation: "D39",
    costCenter: "3255100001",
    isBookable: "Yes",
    checkInRequired: "Yes",
    checkInStartPeriod: "15 min",
    checkInGracePeriod: "60 min",
    isHazardous: "No",
    shouldAssisted: "No",
    isVisualized: "Yes",
    inventoryNumber: "120001",
    network: "Molecular",
    configurationDetails: "configuration details 01",
    labArea: "132-E200-C09-34",
    description: "Test description",
    dateOfLastCalibration: "24-03-2025",
    equipmentType: "C",
    portability: "Portable",
    maintenancePerformedBy: "PAKO",
  },
  {
    linkedInstance: "CLP",
    secondaryIDMapping: "equipmentId",
    equipmentCategory: "Pipette",
    manufacturer: "Eppendorf",
    equipmentModel: "Reference 2",
    variant: "0-50L",
    siteName: "Penzberg",
    serialNumber: "100120231",
    equipmentNickName: "Tesoro",
    equipmentId: "6005289921",
    belongingToGroup: "DSRCMS",
    buildingLocation: "991",
    floor: "Haus B+C",
    room: "C 220",
    responsiblePerson: "tiemannb",
    systemOwner: "quints",
    responsibleProxy: "totht2",
    sop: "F_RT0020_SOP_DE",
    gxpRelevant: "Yes",
    qualificationStatus: "Qualified",
    systemStatus: "Ready to use",
    isBookable: "No",
    isVisualized: "No",
    inventoryNumber: "1200011",
    network: "CoreLab",
    labArea: "132-E200-C09-35",
    contactPerson: "Pako",
  },
  {
    linkedInstance: "None",
    // The one row showing the other shape a secondary-ID mapping takes: a semicolon-separated
    // list of the fields that together identify an equipment with no instance to key it by.
    secondaryIDMapping: "Manufacturer; Model; serialNumber",
    equipmentCategory: "Analyzer",
    manufacturer: "Roche",
    equipmentModel: "cobas 5800",
    variant: "Pilot",
    siteName: "Rotkreuz",
    serialNumber: "100120232",
    equipmentNickName: "Klippklopp",
    belongingToGroup: "DSRCH",
    buildingLocation: "Let-1",
    floor: "EG",
    room: "130",
    responsiblePerson: "Hagemann/Richter",
    systemOwner: "Hagemann/Richter",
    responsibleProxy: "Robert Jenkin",
    sop: "F_RT0006_SOP_DE,F_RT0020_SOP_DE",
    gxpRelevant: "Yes",
    qualificationStatus: "Calibrated",
    dateOfLastMaintanance: "19-02-2022",
    softwareVersion: "1",
    configurationBaseline: "3.5",
    systemStatus: "Out of service",
    functionalLocation: "D39",
    costCenter: "3255100001",
    isBookable: "Yes",
    checkInRequired: "Yes",
    checkInStartPeriod: "45 min",
    checkInGracePeriod: "15 min",
    isHazardous: "Yes",
    shouldAssisted: "No",
    isVisualized: "Yes",
    inventoryNumber: "12000111",
    configurationDetails: "configuration details 02",
    labArea: "132-E200-C09-36",
    dateOfLastCalibration: "24-03-2025",
    equipmentType: "I",
    portability: "Portable",
    maintenancePerformedBy: "Pako Kozo",
  },
  {
    linkedInstance: "RAM Maintenance DB",
    secondaryIDMapping: "equipmentId",
    equipmentCategory: "Analyzer",
    manufacturer: "Roche",
    equipmentModel: "cobas Liat analyzer",
    variant: "3424234",
    siteName: "Pleasanton",
    serialNumber: "123123123",
    materialNumber: "56834054350",
    equipmentNickName: "littleLiat",
    instrumentGTIN: "875423953",
    equipmentId: "PLS-9999999",
    instrumentRUDI: "N/A",
    cluster: "Cluster1",
    comment: "comment",
    belongingToGroup: "DSI R & D Services",
    buildingLocation: "PL726",
    floor: "2",
    room: "4139",
    responsiblePerson: "Peter Maffay",
    systemOwner: "Horst Seehofer",
    responsibleProxy: "Otto Walkes",
    gxpRelevant: "Yes",
    qualificationStatus: "Qualified",
    softwareVersion: "1.2.34",
    configurationBaseline: "Baseline1",
    systemStatus: "Ready to use",
    costCenter: "2232",
    isBookable: "Yes",
    checkInRequired: "Yes",
    checkInStartPeriod: "30 min",
    checkInGracePeriod: "30 min",
    isHazardous: "No",
    shouldAssisted: "Yes",
    isVisualized: "No",
    inventoryNumber: "324324",
    labArea: "132-E200-C09",
    description: "Test description 2",
    contactPerson: "Pako",
    portability: "Portable",
  },
  {
    linkedInstance: "Point of Care RK",
    secondaryIDMapping: "equipmentId",
    equipmentCategory: "Analyzer",
    manufacturer: "Roche",
    equipmentModel: "cobas Liat analyzer",
    variant: "blue",
    siteName: "Rotkreuz",
    serialNumber: "1003",
    materialNumber: "5656222",
    equipmentNickName: "miniLiat",
    instrumentGTIN: "66997464",
    equipmentId: "600515444",
    instrumentRUDI: "N/A",
    cluster: "DSRC-5-1",
    comment: "comment",
    belongingToGroup: "DSI R & D Services",
    buildingLocation: "1",
    floor: "DG",
    room: "7",
    responsiblePerson: "Peter Maffay",
    systemOwner: "Horst Seehofer",
    responsibleProxy: "Otto Walkes",
    gxpRelevant: "Yes",
    qualificationStatus: "Qualified",
    softwareVersion: "2.4",
    configurationBaseline: "Baseline2",
    systemStatus: "Ready to use",
    costCenter: "8888",
    isBookable: "Yes",
    checkInRequired: "Yes",
    checkInStartPeriod: "30 min",
    checkInGracePeriod: "30 min",
    isHazardous: "No",
    shouldAssisted: "No",
    isVisualized: "No",
    inventoryNumber: "324324",
    network: "Point of Care",
    configurationDetails: "configuration details 01",
    labArea: "132-E200-C10",
    dateOfLastCalibration: "24-03-2025",
    equipmentType: "L",
    portability: "Non-portable",
  },
  {
    linkedInstance: "SED",
    secondaryIDMapping: "equipmentId",
    equipmentCategory: "Analyzer",
    manufacturer: "Roche",
    equipmentModel: "cobas Liat analyzer",
    variant: "blue",
    siteName: "Basel pRED",
    serialNumber: "321322",
    materialNumber: "3434321",
    equipmentNickName: "Freddy",
    instrumentGTIN: "432423",
    equipmentId: "600324532",
    instrumentRUDI: "N/A",
    cluster: "Cluster1",
    comment: "comment",
    belongingToGroup: "pRED",
    buildingLocation: "101",
    floor: "UG01",
    room: "1",
    responsiblePerson: "Albert Einstein",
    systemOwner: "Erwin Schroedinger",
    responsibleProxy: "Marie Curie",
    gxpRelevant: "Yes",
    qualificationStatus: "Qualified",
    softwareVersion: "1.2.34",
    configurationBaseline: "Baseline1",
    systemStatus: "Ready to use",
    costCenter: "2232",
    isBookable: "Yes",
    checkInRequired: "Yes",
    checkInStartPeriod: "30 min",
    checkInGracePeriod: "30 min",
    isHazardous: "Yes",
    shouldAssisted: "Yes",
    isVisualized: "No",
    inventoryNumber: "10002",
    contactPerson: "Pako",
    equipmentType: "M",
    portability: "Non-portable",
    maintenancePerformedBy: "Test Person",
  },
  {
    linkedInstance: "P1S Bi-Directional",
    secondaryIDMapping: "equipmentId",
    equipmentCategory: "Analyzer",
    manufacturer: "Roche",
    equipmentModel: "cobas Liat analyzer",
    variant: "blue",
    siteName: "Pleasanton",
    serialNumber: "22011992",
    materialNumber: "22011992",
    equipmentNickName: "BMW",
    instrumentGTIN: "46778888",
    equipmentId: "10004532",
    instrumentRUDI: "N/A",
    cluster: "Cluster1",
    comment: "comment",
    belongingToGroup: "DSRC",
    buildingLocation: "OFFSITE",
    floor: "OS",
    room: "OFFSITE",
    responsiblePerson: "Albert Einstein",
    systemOwner: "Erwin Schroedinger",
    responsibleProxy: "Marie Curie",
    gxpRelevant: "Yes",
    qualificationStatus: "Qualified",
    softwareVersion: "1.2.34",
    configurationBaseline: "Baseline1",
    systemStatus: "Ready to use",
    costCenter: "2232",
    isBookable: "Yes",
    checkInRequired: "Yes",
    checkInStartPeriod: "30 min",
    checkInGracePeriod: "30 min",
    isHazardous: "No",
    shouldAssisted: "No",
    isVisualized: "No",
    inventoryNumber: "10002",
    configurationDetails: "Config Details",
    labArea: "22-1-22",
    description: "Test description 3",
    dateOfLastCalibration: "24-03-2025",
  },
  {
    linkedInstance: "P1S Uni-Directional",
    secondaryIDMapping: "equipmentId",
    equipmentCategory: "Analyzer",
    manufacturer: "Roche",
    equipmentModel: "cobas Liat analyzer",
    variant: "blue",
    siteName: "Pleasanton",
    serialNumber: "22011992",
    materialNumber: "22011992",
    equipmentNickName: "BMW 1800",
    instrumentGTIN: "46778888",
    equipmentId: "10004532",
    instrumentRUDI: "N/A",
    cluster: "Cluster1",
    comment: "comment",
    belongingToGroup: "DSRC",
    buildingLocation: "OFFSITE",
    floor: "OS",
    room: "OFFSITE",
    responsiblePerson: "Albert Einstein",
    systemOwner: "Erwin Schroedinger",
    responsibleProxy: "Marie Curie",
    gxpRelevant: "Yes",
    qualificationStatus: "Qualified",
    softwareVersion: "1.2.34",
    configurationBaseline: "Baseline1",
    systemStatus: "Ready to use",
    costCenter: "2232",
    isBookable: "Yes",
    checkInRequired: "Yes",
    checkInStartPeriod: "30 min",
    checkInGracePeriod: "30 min",
    isHazardous: "No",
    shouldAssisted: "No",
    isVisualized: "No",
    inventoryNumber: "10002",
    configurationDetails: "Config Details",
    labArea: "22-1-22",
    contactPerson: "Pako",
    equipmentType: "T",
    portability: "Non-portable",
  },
];

/**
 * The action a file represents, which is decided by which structure it turns out to be.
 *
 * A bulk-change file carries `id` in every row and exists to change equipment that already
 * exists; a template file has no `id` column at all, so every row of it is a create. The two
 * are told apart by their label rows — see `identifyCsvFormat`.
 */
export enum EquipmentCsvActions {
  CREATE = "create",
  CHANGE = "change",
}

/** Structure and flow per action, for building the managers an import chooses between. */
export const equipmentCsvFormats = [
  {
    action: EquipmentCsvActions.CHANGE,
    structure: equipmentCsvStructure,
    flow: CsvFlows.IMPORT,
  },
  {
    action: EquipmentCsvActions.CREATE,
    structure: equipmentTemplateCsvStructure,
    flow: CsvFlows.TEMPLATE,
  },
];

/**
 * The few fields whose CSV behaviour differs between flows.
 *
 * `id` is the only divergence, and only in where it is read from: the export index keys the
 * document `_id` while the bulk-change index keys it `id`.
 */
export const equipmentCsvFlowOverrides: CsvFlowOverrides = {
  [CsvFlows.EXPORT]: {
    id: { csv: { storageKey: "_id" } },
  },
};
